import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import classNames from 'classnames';
import { loader } from 'graphql.macro';
import { useFlags } from 'launchdarkly-react-client-sdk';
import compose from 'lodash/flowRight';
import { utc } from 'moment';
import { useSnackbar } from 'notistack';
import { useMutation } from '@apollo/react-hooks';
import { Tooltip } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import { makeStyles } from '@material-ui/core/styles';

import { chatClient } from 'src/config/Apollo';
import { colors } from 'src/themes/colors';
import { ReactComponent as EditPencil } from 'src/assets/svgs/edit-pencil.svg';
import { ChatTextContent } from 'src/components/Chat/ChatTextContent';
import { store } from 'src/config/Store';
import {
  MEDITATION_RESOURCE_TYPE,
  PROGRAM_RESOURCE_TYPE,
  BENEFIT_RESOURCE_TYPE,
} from 'src/constants';
import { GET_MESSAGES } from 'src/graphql/chat/queries';
import { Message } from 'src/models/Chat';
import { getActiveConversation } from 'src/selectors/chatSelectors';
import { getCurrentUser } from 'src/selectors/userSelectors';

import AvatarInitials, { AVATAR_SIZE } from '../AvatarInitials';
import ChatResourceItem from './ChatResourceItem';

export const UPDATE_CHAT_MESSAGE_MUTATION = loader('./documents/UpdateChatMessage.gql');

const MARGIN_BETWEEN_CHAT_BOX_AND_AVATAR_INITIALS = 16;
const BORDER_RADIUS_MESSAGE_CONTENT_BOX = 12;
const SENT_AT_MARGIN = AVATAR_SIZE + MARGIN_BETWEEN_CHAT_BOX_AND_AVATAR_INITIALS;

const useStyles = makeStyles((theme) => ({
  messageContainerSentMessage: {
    alignItems: 'flex-end',
  },
  topRowContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
  },
  topRowContainerSentMessage: {
    flexDirection: 'row-reverse',
  },
  messageContentBox: {
    maxWidth: 500,
    flexGrow: 1,
    backgroundColor: 'white',
    borderRadius: `${BORDER_RADIUS_MESSAGE_CONTENT_BOX}px ${BORDER_RADIUS_MESSAGE_CONTENT_BOX}px ${BORDER_RADIUS_MESSAGE_CONTENT_BOX}px 0px`,
    padding: 18,
    boxShadow: '0 7px 12px 2px rgba(216, 216, 216, 0.3)',
    marginLeft: MARGIN_BETWEEN_CHAT_BOX_AND_AVATAR_INITIALS,
    [theme.breakpoints.down('xs')]: {
      maxWidth: 215,
      marginLeft: 0,
    },
  },
  messageContentBoxSentMessage: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: `${BORDER_RADIUS_MESSAGE_CONTENT_BOX}px ${BORDER_RADIUS_MESSAGE_CONTENT_BOX}px 0px ${BORDER_RADIUS_MESSAGE_CONTENT_BOX}px`,
    marginRight: MARGIN_BETWEEN_CHAT_BOX_AND_AVATAR_INITIALS,
    marginLeft: 0,
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
    },
  },
  messageContent: {
    color: colors.gray5,
    whiteSpace: 'pre-wrap',
  },
  messageContentSentMessage: {
    color: 'white',
  },
  alignRight: {
    justifyContent: 'flex-end',
  },
  sentAtContainer: {
    marginLeft: SENT_AT_MARGIN,
    marginTop: 13,
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },
  },
  sentAtContainerSentMessage: {
    marginRight: SENT_AT_MARGIN,
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
    },
  },
  metaInfo: {
    color: colors.gray5,
    marginRight: 5,
  },
  coachMessageLink: {
    color: colors.white1,
    fontWeight: 600,
  },
  memberMessageLink: {
    color: colors.blue1,
    fontWeight: 600,
  },
}));

enum ChatMessageTestID {
  Container = 'ChatMessageTestID.Container',
  ReadAt = 'ChatMessageTestID.ReadAt',
  SentAt = 'ChatMessageTestID.SentAt',
}

interface UpdateChatMessageMutationData {
  updateMessage: { ok: boolean };
}

interface UpdateChatMessageMutationVars {
  conversationUuid: string;
  messageUuid: string;
  content: string;
}

interface GetMessagesQueryData {
  getMessages: {
    messages: Message[];
  };
}

const ChatMessageRow = ({ messageData, currentUser, meditationMap, programMap, benefitMap }) => {
  const { editChat } = useFlags();
  const activeConversation = useSelector(getActiveConversation);
  const snackbar = useSnackbar();
  const classes = useStyles();
  const [hovering, setHovering] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const self = useSelector(getCurrentUser);

  const [updateChatMessage] = useMutation<
    UpdateChatMessageMutationData,
    UpdateChatMessageMutationVars
  >(UPDATE_CHAT_MESSAGE_MUTATION, { client: chatClient });

  useEffect(() => {
    setEditedText(messageData.content);
  }, [messageData.content]);

  if (!activeConversation) {
    return null;
  }

  const handleSaveChatEdit = async () => {
    try {
      await updateChatMessage({
        variables: {
          conversationUuid: activeConversation.uuid,
          messageUuid: messageData.uuid,
          content: editedText,
        },
        update: (cache, mutationResult) => {
          if (!mutationResult.data?.updateMessage.ok) {
            return;
          }
          // CI-1634: Chat context does not update when the cache is updated,
          // so chat edits will not show until the update subscription is triggered.
          // When CI-1634 is resolved, this should update the message in UI on success.
          const cachedResult = cache.readQuery<GetMessagesQueryData>({
            query: GET_MESSAGES,
            variables: { conversationUuid: activeConversation.uuid, after: null },
          });
          const cachedMessages = cachedResult!.getMessages.messages.map((cachedMessage) => {
            if (cachedMessage.uuid === messageData.uuid) {
              return { ...cachedMessage, content: editedText };
            }
            return cachedMessage;
          });

          cache.writeQuery({
            query: GET_MESSAGES,
            variables: { conversationUuid: activeConversation.uuid, after: null },
            data: {
              ...cachedResult,
              getMessages: {
                ...cachedResult!.getMessages,
                messages: cachedMessages,
              },
            },
          });
        },
      });
      setEditing(false);
    } catch {
      snackbar.enqueueSnackbar("We couldn't update your message. Please try again later.", {
        variant: 'error',
      });
    }
  };

  const sender = self.uuid === messageData.senderUuid ? self : activeConversation.member;
  const currentUserIsSender = sender.uuid === currentUser.uuid;

  const renderResource = (resource) => (
    <Grid item xs={6} key={resource.uniqueKey}>
      <ChatResourceItem
        key={resource.uniqueKey}
        item={resource}
        noClick
        useMargin
        maxWidth={undefined}
        removeResource={undefined}
      />
    </Grid>
  );

  const resources = messageData.messageResources
    .sort((a, b) => a.order - b.order)
    .filter(
      (resource) =>
        resource.meditationId || resource.programId || resource.companyAdditionalBenefitId,
    )
    .map((messageResource) => {
      const [resource, resourceType] =
        (messageResource.meditationId && [
          meditationMap[messageResource.meditationId],
          MEDITATION_RESOURCE_TYPE,
        ]) ||
        (messageResource.programId && [
          programMap[messageResource.programId],
          PROGRAM_RESOURCE_TYPE,
        ]) ||
        (messageResource.companyAdditionalBenefitId && [
          benefitMap[messageResource.companyAdditionalBenefitId],
          BENEFIT_RESOURCE_TYPE,
        ]);

      return {
        ...resource,
        resourceType,
        uniqueKey: messageResource.uuid,
      };
    });

  // when there is only one message-resource, and the message is sent by the currentUser,
  // align the messageResource right for better UI.
  const alignResourceRight = resources.length === 1 && currentUserIsSender;

  return (
    <div>
      {(messageData.content || resources.length > 0) && (
        <div
          className={classNames('flex flex-col py-6 px-8 rounded justify-items-start', {
            [classes.messageContainerSentMessage]: currentUserIsSender,
            'hover:bg-gray-200': editChat,
            'bg-gray-200': editing,
          })}
          data-testid={ChatMessageTestID.Container}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
        >
          <div
            className={classNames(classes.topRowContainer, {
              [classes.topRowContainerSentMessage]: currentUserIsSender,
            })}
          >
            <Hidden xsDown>
              <AvatarInitials
                initials={`${sender.firstName[0]}${sender.lastName[0]}`}
                isSelected={false}
              />
            </Hidden>
            {messageData.content && (
              <>
                <div className="flex max-w-chat w-9/12">
                  <ChatTextContent
                    editable={editing}
                    isSender={currentUserIsSender}
                    content={messageData.content}
                    onChange={(message) => {
                      setEditedText(message);
                    }}
                  />
                </div>
                {editChat && hovering && currentUserIsSender && !editing && (
                  <div className="self-center mr-6">
                    <Tooltip title="Edit message" placement="top" arrow>
                      <button
                        className="rounded-full bg-white p-3"
                        onClick={() => setEditing(true)}
                      >
                        <EditPencil />
                      </button>
                    </Tooltip>
                  </div>
                )}
              </>
            )}
          </div>
          <Grid
            container
            className={classNames({
              [classes.alignRight]: alignResourceRight,
            })}
          >
            {resources.map(renderResource)}
          </Grid>

          <div
            className={classNames(classes.sentAtContainer, {
              [classes.sentAtContainerSentMessage]: currentUserIsSender,
            })}
          >
            {editing && (
              <div className="inline-flex flex-row-reverse mr-2">
                <button
                  className="bg-blue-400 text-white p-1 px-4 rounded text-sm font-extrabold ml-1"
                  onClick={handleSaveChatEdit}
                >
                  Save
                </button>
                <button
                  className="bg-white text-blue-400 p-1 px-4 text-sm font-extrabold border border-gray-400 rounded"
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </button>
              </div>
            )}
            {!editing && (
              <>
                <p
                  className="mr-2 text-gray-500 font-medium"
                  data-testid={ChatMessageTestID.SentAt}
                >
                  <span className="text-gray-400">{'Sent: '}</span>
                  {utc(messageData.sentAt).local().format('MMM Do YYYY, LT')}
                </p>
                {messageData.readAt && (
                  <>
                    <div style={{ width: '1px' }} className="h-4 bg-gray-400 mr-2" />
                    <p className="text-gray-500 font-medium" data-testid={ChatMessageTestID.ReadAt}>
                      <span className="text-gray-400">{'Read: '}</span>
                      {utc(messageData.readAt).local().format('MMM Do YYYY, LT')}
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const mapState = (state) => ({
  currentUser: state.user.currentUser,
  meditationMap: store.select.resources.meditationMap(state),
  programMap: store.select.resources.programMap(state),
  benefitMap: store.select.resources.benefitMap(state),
});

export default compose(connect(mapState))(ChatMessageRow);
